import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { DIALOG_WIDTH } from "../../constants";
import StandardDialogTitle from "./StandardDialogTitle";
import StandardDialogActions from "./StandardDialogActions";
import * as React from "react";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useCreateAlert } from "../../hooks/mutation/create/useCreateAlert";
import { useState } from "react";
import { Integration } from "../../types";
import { useAlertsFromIntegration } from "../../hooks/query/useAlertsFromIntegration";
import { IntegrationPicker } from "../IntegrationPicker";
import { ColumnDef, DataTable } from "../Table";
import { AlertClientResponse } from "../../clients/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport } from "@fortawesome/free-solid-svg-icons";
import { useIntegrations } from "../../hooks/query/useIntegrations";

const ImportAlertsDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  integrationId?: string;
}> = ({ onDialogClosed, integrationId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { mutateAsync: createAlert, isPending: isCreating } = useCreateAlert();
  const { data: integrations = [] } = useIntegrations();
  const setupIntegrations = React.useMemo(() => {
    return integrations.filter((row) => row.auth?.ready === true);
  }, [integrations]);

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const [integration, setIntegration] = useState<Integration | null>(null);

  const { data: alertsFromProvider = [], isLoading } = useAlertsFromIntegration(
    integration ?? undefined
  );

  const [selectedAlertIds, setSelectedAlertIds] = useState<string[]>([]);

  const handleSelectionChange = React.useCallback((ids: string[]) => {
    setSelectedAlertIds((current) => {
      if (current.length !== ids.length) {
        return ids;
      }

      for (let i = 0; i < current.length; i += 1) {
        if (current[i] !== ids[i]) {
          return ids;
        }
      }

      return current;
    });
  }, []);

  React.useEffect(() => {
    if (!integrationId) {
      return;
    }
    if (integration) {
      return;
    }

    const preselected = setupIntegrations.find((row) => row.id === integrationId);
    if (!preselected) {
      return;
    }

    setIntegration(preselected);
  }, [integration, integrationId, setupIntegrations]);

  const onClose = () => {
    setSubmitError(null);
    setIntegration(null);
    setSelectedAlertIds([]);
    onDialogClosed();
  };

  const handleImport = async () => {
    try {
      setSubmitError(null);

      if (!integration) {
        setSubmitError("Missing integration");
        return;
      }

      if (selectedAlertIds.length === 0) {
        setSubmitError("Select at least one alert");
        return;
      }

      const selectedAlerts = alertsFromProvider.filter((a) => selectedAlertIds.includes(a.id));
      for(const a of selectedAlerts) {
          await createAlert({
            name: a.label,
            description: undefined,
            foreignKeyValue: a.id,
            integrationId: integration.id,
          })
      }

      onDialogClosed({
        message: `Imported ${selectedAlerts.length} alerts`,
        severity: "success",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const columns: ColumnDef<AlertClientResponse>[] = [
    {
      id: "name",
      header: "Name",
      render: (row) => row.label,
    },
    {
      id: "id",
      header: "Provider alert id",
      render: (row) => row.id,
    },
  ];

  const colSpan = 1 + columns.length;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle title={"Import alerts"} isSmallScreen={isSmallScreen} onClose={onClose} />

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleImport();
        }}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ display: "grid", placeItems: "center" }}>
            <Stack sx={{ width: "100%" }}>
              <Stack spacing={2.25} sx={{ width: "100%" }}>
                <FormControl>
                  <FormLabel>Integration</FormLabel>

                  <IntegrationPicker
                    value={integration?.id ?? ""}
                    onChange={(nextIntegration: Integration | undefined) => {
                      setIntegration(nextIntegration ?? null);
                      setSelectedAlertIds([]);
                      if (submitError) { 
                        setSubmitError(null);
                      }
                    }}
                  />
                </FormControl>

                {integration && <DataTable<AlertClientResponse>
                  title="Alerts"
                  loading={isLoading}
                  subtitle={
                    isLoading
                      ? "Loading..."
                      : integration
                        ? undefined
                        : "Select an integration to load alerts"
                  }
                  rows={integration ? alertsFromProvider : []}
                  columns={columns}
                  colSpan={colSpan}
                  selectable={true}
                  actions={false}
                  onSelectionChange={handleSelectionChange}
                />}

                {submitError && (
                  <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                    {submitError}
                  </FormHelperText>
                )}
              </Stack>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Import"}
          primaryIcon={<FontAwesomeIcon icon={faFileImport} />}
          onCancel={onClose}
          loading={isCreating}
          disabled={isCreating}
        />
      </Box>
    </Dialog>
  );
};

export default ImportAlertsDialog;
